"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.opBillEditPendingQuerySchema = exports.opBillEditRecordQuerySchema = exports.opBillEditPayloadSchema = exports.requestOpBillEditSchema = exports.previewOpBillEditSchema = exports.opBillEditGroupsShape = exports.WALKIN_GENDERS = exports.opBillEditTargetKindEnum = void 0;
/**
 * CASH COUNTER BILL EDIT REQUEST (CASHEDIT, desk 2026-10-18: "go for cash counter edit flow" - the same promise as the patient edit and the
 * appointment edit: whatever the counter typed can be corrected, through Request -> Verify -> Approve, by the user who created the receipt).
 *
 * ONE REQUEST, ONE RECEIPT, ONLY WHAT DIFFERS. The body names the receipt (an OP receipt / walk-in receipt, or an IP advance receipt), the
 * version the clerk was looking at, and the groups that change. An absent group is left alone; inside a group, an absent field is left alone
 * and `null` clears a field that may be empty. A reason is required on the request itself - it is what the verifier and approver read.
 * Nothing on the receipt changes until approval.
 *
 * THIS FILE GROWS BY SLICES, each a group the SAME request carries:
 *   slice 1 (details that move no money)   walkIn, newborn, creditParty, lineDoctors, lineRemarks (OP receipt) - paymentKind, notes (IP advance)
 *   slice 2 (payment amounts / tender)     paymentLines (OP receipt): the amount, mode, bank account and reference of each payment line
 *   slice 3 (discount / concession)        discounts: a line's own discount and the bill-level discount, as a percent or an amount
 *   slice 4 (service lines)                serviceLines: change a line's quantity (and, on an open-value line, its rate), remove a line, add a line
 *
 * Slices 3-4 correct the BILL ITSELF, in place, under the SAME receipt number (owner, 2026-10-18: "keep receipt number same and update the field
 * values as per correction approved"): the server recomputes the bill from the figures it stored, rewrites it, reverses the bill's journal entry and
 * posts the corrected one. The screen sends only what the clerk typed - never a computed amount.
 */
const zod_1 = require("zod");
const lab_newborn_1 = require("../../domain/lab-newborn");
const ip_settlement_schema_1 = require("../../schemas/ip-settlement.schema");
const uuid = zod_1.z.string().uuid();
exports.opBillEditTargetKindEnum = zod_1.z.enum(['OP_BILL', 'IP_ADVANCE']);
/** The three words the walk-in gender box offers (stored verbatim, matched to the GENDER lookup by name). */
exports.WALKIN_GENDERS = ['Male', 'Female', 'Other'];
const PHONE_PATTERN = /^\+?[0-9 ()-]{5,20}$/;
const walkInEditSchema = zod_1.z
    .object({
    /** `null` clears it. */
    mobile: zod_1.z.string().trim().regex(PHONE_PATTERN, 'Enter a valid phone number').nullable().optional(),
    ageYears: zod_1.z.number().int().min(0).max(150).nullable().optional(),
    gender: zod_1.z.enum(exports.WALKIN_GENDERS).nullable().optional(),
})
    .strict();
const newbornEditSchema = zod_1.z
    .object({
    name: zod_1.z.string().trim().min(2).max(120).optional(),
    ageDays: zod_1.z.number().int().min(0).max(90).optional(),
    gender: zod_1.z.enum(lab_newborn_1.NEWBORN_GENDERS).optional(),
})
    .strict();
/** Who owes a bill passed to credit. The kind (employee / company) stays what it was - a bill is never moved between the two here. */
const creditPartyEditSchema = zod_1.z
    .object({
    employeeId: uuid.optional(),
    payerId: uuid.optional(),
})
    .strict()
    .refine((value) => !(value.employeeId && value.payerId), { message: 'Send employeeId or payerId, never both' })
    .refine((value) => Boolean(value.employeeId || value.payerId), { message: 'Name the employee or the company' });
/**
 * ONE PAYMENT LINE of an OP receipt (slice 2). Absent = leave alone; the bank account and the reference are `null` to clear them (a cash line has
 * neither). The server validates the resulting tender exactly as collection does (a non-cash mode needs its bank account and UTR) and refuses a
 * change that would leave the bill paid for more than it is worth.
 */
const paymentLineEditSchema = zod_1.z
    .object({
    paymentLineId: uuid,
    amountPaise: zod_1.z.number().int().positive().max(100_000_000).optional(),
    paymentModeId: uuid.optional(),
    bankAccountId: uuid.nullable().optional(),
    referenceNumber: zod_1.z.string().trim().max(100).nullable().optional(),
})
    .strict();
/** A discount is a percent (0-100, two decimals) OR an amount in whole paise - never both. Neither = take the discount off. */
const percentValue = zod_1.z
    .number()
    .min(0)
    .max(100)
    .refine((value) => Math.round(value * 100) / 100 === value, { message: 'A percent has at most two decimals' });
const discountValue = {
    percent: percentValue.optional(),
    amountPaise: zod_1.z.number().int().min(0).max(1_000_000_000).optional(),
};
const oneOfPercentOrAmount = (value) => !(value.percent !== undefined && value.amountPaise !== undefined);
const BOTH_MESSAGE = { message: 'Send a percent or an amount, never both' };
/** SLICE 3. One line's OWN discount. */
const lineDiscountEditSchema = zod_1.z.object({ lineId: uuid, ...discountValue }).strict().refine(oneOfPercentOrAmount, BOTH_MESSAGE);
/** SLICE 3. The discount on the WHOLE bill (deducted once, at the footer). Present with neither value = take it off. */
const billDiscountEditSchema = zod_1.z.object(discountValue).strict().refine(oneOfPercentOrAmount, BOTH_MESSAGE);
const discountsEditSchema = zod_1.z
    .object({
    lines: zod_1.z.array(lineDiscountEditSchema).max(100).optional(),
    bill: billDiscountEditSchema.optional(),
})
    .strict();
/** SLICE 4. A catalogue service added to the bill: priced by the server as of the ORIGINAL bill date (a rate is sent only for an open-value service). */
const addedServiceSchema = zod_1.z
    .object({
    chargeId: uuid,
    quantity: zod_1.z.number().positive().max(100_000),
    unitRatePaise: zod_1.z.number().int().positive().max(1_000_000_000).optional(),
    doctorId: uuid.optional(),
    remarks: zod_1.z.string().trim().max(500).optional(),
})
    .strict();
/** SLICE 4. An existing line: its quantity and - for an open-value service only - its rate. */
const lineChangeSchema = zod_1.z
    .object({
    lineId: uuid,
    quantity: zod_1.z.number().positive().max(100_000).optional(),
    unitRatePaise: zod_1.z.number().int().positive().max(1_000_000_000).optional(),
})
    .strict()
    .refine((value) => value.quantity !== undefined || value.unitRatePaise !== undefined, { message: 'Name what changes: the quantity or the rate' });
const serviceLinesEditSchema = zod_1.z
    .object({
    change: zod_1.z.array(lineChangeSchema).max(100).optional(),
    remove: zod_1.z.array(uuid).max(100).optional(),
    add: zod_1.z.array(addedServiceSchema).max(20).optional(),
})
    .strict();
const lineDoctorEditSchema = zod_1.z.object({ lineId: uuid, doctorId: uuid.nullable() }).strict();
const lineRemarksEditSchema = zod_1.z.object({ lineId: uuid, remarks: zod_1.z.string().trim().max(500).nullable() }).strict();
/** Every group a request can carry. Each is optional; a request with none is "nothing changed". */
exports.opBillEditGroupsShape = {
    walkIn: walkInEditSchema.optional(),
    newborn: newbornEditSchema.optional(),
    creditParty: creditPartyEditSchema.optional(),
    lineDoctors: zod_1.z.array(lineDoctorEditSchema).max(100).optional(),
    lineRemarks: zod_1.z.array(lineRemarksEditSchema).max(100).optional(),
    /** OP receipt only (slice 2): the payment lines whose amount / tender is being corrected. A money change - it posts a balancing journal entry. */
    paymentLines: zod_1.z.array(paymentLineEditSchema).max(20).optional(),
    /** OP receipt only (slice 3): discounts, line by line and on the whole bill. Corrects the bill itself - the books are reversed and re-posted. */
    discounts: discountsEditSchema.optional(),
    /** OP receipt only (slice 4): quantities, rates, removed and added services. Corrects the bill itself - the books are reversed and re-posted. */
    serviceLines: serviceLinesEditSchema.optional(),
    /** IP advance only: "Payment For" (Advance / Final Payment) and the free-text Remarks. */
    paymentKind: zod_1.z.enum(ip_settlement_schema_1.IP_PAYMENT_KINDS).optional(),
    notes: zod_1.z.string().trim().max(2000).nullable().optional(),
};
const targetShape = {
    kind: exports.opBillEditTargetKindEnum,
    /** `FinInvoice.id` for an OP receipt / walk-in receipt. */
    invoiceId: uuid.optional(),
    /** `IpAdmissionDeposit.id` for an IP advance receipt. */
    depositId: uuid.optional(),
    /** `FinInvoice.version` / `IpAdmissionDeposit.lockVersion` the clerk was looking at - a receipt that moved since is refused. */
    expectedVersion: zod_1.z.number().int().min(0),
};
function targetMatchesKind(value) {
    return value.kind === 'OP_BILL' ? Boolean(value.invoiceId) && !value.depositId : Boolean(value.depositId) && !value.invoiceId;
}
const TARGET_MESSAGE = { message: 'An OP receipt needs invoiceId, an IP advance needs depositId - never both', path: ['kind'] };
exports.previewOpBillEditSchema = zod_1.z.object({ ...targetShape, ...exports.opBillEditGroupsShape }).refine(targetMatchesKind, TARGET_MESSAGE);
exports.requestOpBillEditSchema = zod_1.z
    .object({ ...targetShape, ...exports.opBillEditGroupsShape, reason: zod_1.z.string().trim().min(3, 'Give the reason for the change').max(500) })
    .refine(targetMatchesKind, TARGET_MESSAGE);
/** The stored/replayed form: the request without its reason (what approval applies). */
exports.opBillEditPayloadSchema = zod_1.z.object({ ...targetShape, ...exports.opBillEditGroupsShape }).refine(targetMatchesKind, TARGET_MESSAGE);
exports.opBillEditRecordQuerySchema = zod_1.z
    .object({ kind: exports.opBillEditTargetKindEnum, invoiceId: uuid.optional(), depositId: uuid.optional() })
    .refine(targetMatchesKind, TARGET_MESSAGE);
exports.opBillEditPendingQuerySchema = exports.opBillEditRecordQuerySchema;
//# sourceMappingURL=financial-op-bill-edit.request.js.map